import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight, oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useLanguage } from "../../i18n/LanguageProvider";
import { usePermissions } from "../../hooks/usePermissions";

type Lang = "curl" | "python" | "csharp" | "typescript";

interface Endpoint {
  method: "GET";
  path: string;
  summary: string;
  /** Resource key required to actually call the endpoint (matches server). */
  resource: "electricity";
  params: { name: string; required: boolean; example: string; description: string }[];
  example: string; // example query string (after ?)
}

const ENDPOINTS: Endpoint[] = [
  {
    method: "GET",
    path: "/api/v1/prices/electricity",
    summary: "Historiska spotpriser för el (Nord Pool).",
    resource: "electricity",
    params: [
      { name: "startTime", required: true, example: "2026-05-01T00:00:00Z", description: "ISO 8601 starttid (inklusive)." },
      { name: "endTime", required: true, example: "2026-05-07T23:59:59Z", description: "ISO 8601 sluttid (inklusive)." },
      { name: "area", required: false, example: "SE3", description: "Filtrera på prisområde (SE1–SE4). Utelämna för alla." },
    ],
    example: "startTime=2026-05-01T00:00:00Z&endTime=2026-05-07T23:59:59Z&area=SE3",
  },
  {
    method: "GET",
    path: "/api/v1/predictions/electricity",
    summary: "Prognoser för elspot (timupplösning, närmaste månaden).",
    resource: "electricity",
    params: [
      { name: "area", required: false, example: "SE3", description: "Filtrera på prisområde." },
      { name: "generatedAt", required: false, example: "latest", description: "ISO-timestamp för en specifik prognoskörning, eller 'latest' (standard)." },
    ],
    example: "area=SE3&generatedAt=latest",
  },
];

const PRISM_LANG: Record<Lang, string> = {
  curl: "bash",
  python: "python",
  csharp: "csharp",
  typescript: "typescript",
};

const buildUrl = (origin: string, ep: Endpoint) => `${origin}${ep.path}?${ep.example}`;

const sample = {
  curl: (url: string) => `curl -H "X-API-Key: $API_KEY" \\
  "${url}"`,
  python: (url: string) => `import os, requests

resp = requests.get(
    "${url}",
    headers={"X-API-Key": os.environ["API_KEY"]},
    timeout=15,
)
resp.raise_for_status()
data = resp.json()
print(data)`,
  csharp: (url: string) => `using System.Net.Http;
using System.Net.Http.Headers;

using var http = new HttpClient();
http.DefaultRequestHeaders.Add("X-API-Key",
    Environment.GetEnvironmentVariable("API_KEY"));

var json = await http.GetStringAsync(
    "${url}");
Console.WriteLine(json);`,
  typescript: (url: string) => `const apiKey = process.env.API_KEY!;

const res = await fetch(
  "${url}",
  { headers: { "X-API-Key": apiKey } },
);
if (!res.ok) throw new Error(\`HTTP \${res.status}\`);
const data = await res.json();
console.log(data);`,
};

interface Props {
  show: boolean;
  onHide: () => void;
}

export const ApiDocsModal = ({ show, onHide }: Props) => {
  const { t } = useLanguage();
  const { hasPermission } = usePermissions();
  const [lang, setLang] = useState<Lang>("curl");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const hasAccess = (resource: Endpoint["resource"]) => hasPermission(resource, "read");

  // Track theme changes so the highlighter swaps between light/dark Prism
  // themes when the user toggles dark mode while the modal is open.
  const [isDark, setIsDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.getAttribute("data-bs-theme") === "dark",
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-bs-theme") === "dark");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => obs.disconnect();
  }, []);
  const prismStyle = isDark ? oneDark : oneLight;

  const origin = useMemo(() => {
    if (typeof window === "undefined") return "https://spotprisprognos.se";
    return `${window.location.protocol}//${window.location.host}`;
  }, []);

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch {
      /* ignore */
    }
  };

  const langTabs: { id: Lang; label: string }[] = [
    { id: "curl", label: "curl" },
    { id: "python", label: "Python" },
    { id: "csharp", label: "C#" },
    { id: "typescript", label: "TypeScript" },
  ];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{t("account.apiDocsTitle") || "API-dokumentation"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="api-docs">
        <div className="api-docs__intro">
          <p>
            {t("account.apiDocsIntro") ||
              "Alla endpoints kräver headern"} <code>X-API-Key: &lt;din nyckel&gt;</code>.
            {" "}
            {t("account.apiDocsBaseUrl") || "Bas-URL"}: <code>{origin}</code>
          </p>
          <div className="sp-alert sp-alert--info">
            {t("account.apiDocsRateLimit") ||
              "Rate limit per nyckel: 1 000 anrop/timme (kontakta admin för högre)."}
          </div>
        </div>

        <div className="sp-tabs api-docs__langtabs" role="tablist">
          {langTabs.map((lt) => (
            <button
              key={lt.id}
              type="button"
              className={`sp-tab ${lang === lt.id ? "is-active" : ""}`}
              onClick={() => setLang(lt.id)}
            >
              {lt.label}
            </button>
          ))}
        </div>

        {ENDPOINTS.map((ep) => {
          const url = buildUrl(origin, ep);
          const code = sample[lang](url);
          const copyKey = `${ep.path}:${lang}`;
          const locked = !hasAccess(ep.resource);
          return (
            <section
              key={ep.path}
              className={`api-docs__endpoint ${locked ? "api-docs__endpoint--locked" : ""}`}
            >
              <header className="api-docs__ep-head">
                <span className="api-docs__method">{ep.method}</span>
                <code className="api-docs__path">{ep.path}</code>
                <span
                  className={`sp-chip ${locked ? "sp-chip--warn" : "sp-chip--success"} api-docs__access-chip`}
                >
                  {locked ? (
                    <>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        aria-hidden="true"
                        style={{ marginRight: 4 }}
                      >
                        <path d="M8 1a3 3 0 0 0-3 3v3H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3zm-2 6V4a2 2 0 1 1 4 0v3H6z" />
                      </svg>
                      {t("account.apiDocsNoAccess") || "Ingen åtkomst"}
                    </>
                  ) : (
                    <>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        aria-hidden="true"
                        style={{ marginRight: 4 }}
                      >
                        <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 1 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0z" />
                      </svg>
                      {t("account.apiDocsHasAccess") || "Åtkomst"}
                    </>
                  )}
                </span>
              </header>
              <p className="api-docs__summary">{ep.summary}</p>

              {locked && (
                <div className="sp-alert sp-alert--warn api-docs__lock-msg">
                  {t("account.apiDocsContactAdmin") ||
                    "Du har inte åtkomst till den här endpointen. Kontakta en administratör."}
                </div>
              )}

              {ep.params.length > 0 && (
                <table className="sp-table api-docs__params">
                  <thead>
                    <tr>
                      <th>{t("account.apiDocsParam") || "Parameter"}</th>
                      <th>{t("account.apiDocsRequired") || "Obligatorisk"}</th>
                      <th>{t("account.apiDocsExample") || "Exempel"}</th>
                      <th>{t("account.apiDocsDescription") || "Beskrivning"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ep.params.map((p) => (
                      <tr key={p.name}>
                        <td><code>{p.name}</code></td>
                        <td>
                          {p.required ? (
                            <span className="sp-chip sp-chip--danger">
                              {t("account.apiDocsYes") || "Ja"}
                            </span>
                          ) : (
                            <span className="sp-chip">{t("account.apiDocsNo") || "Nej"}</span>
                          )}
                        </td>
                        <td><code>{p.example}</code></td>
                        <td>{p.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="api-docs__code">
                <button
                  type="button"
                  className="sp-btn sp-btn--ghost sp-btn--sm api-docs__copy"
                  onClick={() => copy(code, copyKey)}
                >
                  {copiedKey === copyKey
                    ? t("account.apiDocsCopied") || "Kopierat!"
                    : t("account.apiDocsCopy") || "Kopiera"}
                </button>
                <SyntaxHighlighter
                  language={PRISM_LANG[lang]}
                  style={prismStyle}
                  customStyle={{
                    margin: 0,
                    padding: "12px 14px",
                    background: "transparent",
                    fontSize: "0.8rem",
                    lineHeight: 1.5,
                    borderRadius: 0,
                  }}
                  codeTagProps={{
                    style: {
                      fontFamily:
                        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    },
                  }}
                >
                  {code}
                </SyntaxHighlighter>
              </div>
            </section>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <button className="sp-btn sp-btn--primary" onClick={onHide}>
          {t("account.cancel") || "Stäng"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
